<template>
    <v-container :fluid="true" class="max-height activity-scroll">
        <v-row>
            <v-col class="px-5 d-flex justify-space-between align-center flex-wrap ga-2" cols="12">
                <div>
                    <h1 class="text-left text-h5 font-weight-bold">Activity</h1>
                    <p class="text-body-2 text-medium-emphasis text-left mb-0">Trips and alerts across your trackers</p>
                </div>
                <div v-if="alerts.length" class="d-flex ga-2">
                    <v-btn color="success" prepend-icon="mdi-check-all" size="small" variant="tonal"
                           :disabled="loading || !hasUnread" @click="readAll">Mark all read
                    </v-btn>
                    <v-btn color="error" prepend-icon="mdi-notification-clear-all" size="small" variant="tonal"
                           :disabled="loading" @click="clearConfirmDialog = true">Clear alerts
                    </v-btn>
                </div>
            </v-col>
        </v-row>

        <!-- Type + per-member filters -->
        <v-row v-if="!loading && (allItems.length || memberChips.length > 1)">
            <v-col cols="12" class="px-5 d-flex flex-wrap align-center ga-2">
                <v-btn-toggle v-model="typeFilter" mandatory density="comfortable" variant="outlined"
                              color="primary" rounded="lg" class="type-toggle">
                    <v-btn value="all" size="small">All</v-btn>
                    <v-btn value="trip" size="small" prepend-icon="mdi-map-marker-path">Trips</v-btn>
                    <v-btn value="alert" size="small" prepend-icon="mdi-bell">
                        Alerts
                        <v-badge v-if="unreadCount" inline color="error" :content="unreadCount" class="ms-1"/>
                    </v-btn>
                </v-btn-toggle>

                <v-divider v-if="memberChips.length > 1" vertical class="mx-1 my-1"/>

                <template v-if="memberChips.length > 1">
                    <v-chip size="small" :variant="selectedMemberKey === null ? 'flat' : 'outlined'"
                            :color="selectedMemberKey === null ? 'primary' : undefined"
                            @click="selectedMemberKey = null">Everyone</v-chip>
                    <v-chip v-for="m in memberChips" :key="m.key" size="small"
                            :variant="selectedMemberKey === m.key ? 'flat' : 'outlined'"
                            :color="selectedMemberKey === m.key ? 'primary' : undefined"
                            @click="selectedMemberKey = m.key">
                        <v-avatar v-if="m.photo" start size="18" class="me-1">
                            <v-img :src="m.photo" :alt="m.name"/>
                        </v-avatar>
                        {{ m.name }}
                    </v-chip>
                </template>
            </v-col>
        </v-row>

        <!-- Weekly summary (trips) -->
        <v-row v-if="!loading && typeFilter !== 'alert' && filteredTrips.length">
            <v-col cols="12" class="px-5">
                <v-card variant="outlined" rounded="lg" class="pa-3">
                    <div class="d-flex justify-space-between align-center mb-2">
                        <span class="text-subtitle-2 font-weight-bold">This week</span>
                        <span class="text-caption text-medium-emphasis">vs last week</span>
                    </div>
                    <v-row dense>
                        <v-col v-for="tile in weeklyTiles" :key="tile.label" cols="6" sm="3">
                            <div class="text-caption text-medium-emphasis">{{ tile.label }}</div>
                            <div class="d-flex align-center ga-1">
                                <!-- Avg score is insights-gated: show a lock for the
                                     owner (a member on another circle just sees "--"). -->
                                <v-icon v-if="insightsLockedOwner && tile.label === 'Avg score'"
                                        icon="mdi-lock" size="18" color="primary" title="Unlock with Premium"/>
                                <span v-else class="text-h6 font-weight-bold">{{ tile.value }}</span>
                                <v-icon v-if="tile.trend" :icon="tile.trend.icon" size="14"
                                        :color="tile.trend.color" :title="tile.trend.title"/>
                            </div>
                        </v-col>
                    </v-row>
                </v-card>
            </v-col>
        </v-row>

        <!-- Where time was spent (trips) -->
        <v-row v-if="!loading && typeFilter !== 'alert' && placeTotals.length">
            <v-col cols="12" class="px-5">
                <v-card variant="outlined" rounded="lg" class="pa-3">
                    <div class="text-subtitle-2 font-weight-bold mb-2">Where time was spent</div>
                    <div class="d-flex flex-wrap ga-2">
                        <v-chip v-for="p in placeTotals" :key="p.place" size="small" variant="tonal" color="primary">
                            <v-icon start icon="mdi-map-marker" size="14"/>
                            {{ p.place }} &middot; {{ formatDuration(p.seconds) }}
                        </v-chip>
                    </div>
                </v-card>
            </v-col>
        </v-row>

        <!-- Unified feed -->
        <v-row>
            <v-col cols="12" class="px-5 pb-16">
                <div v-if="loading" class="text-center pa-10">
                    <v-progress-circular color="primary" indeterminate size="32"/>
                    <p class="text-body-2 text-medium-emphasis mt-3 mb-0">Loading activity...</p>
                </div>

                <template v-else-if="groupedItems.length">
                    <div v-for="group in groupedItems" :key="group.key" class="mb-2">
                        <div class="text-overline text-medium-emphasis px-1 pt-2">{{ group.label }}</div>

                        <div v-for="item in group.items" :key="item._key">
                            <!-- Trip card -->
                            <v-card v-if="item._kind === 'trip'" variant="outlined" rounded="lg"
                                    class="feed-card trip-card mb-2">
                                <div class="d-flex align-center pa-3 ga-3">
                                    <SubjectAvatar :device="item._device" :size="40" :show-status="false"/>
                                    <div class="flex-grow-1" style="min-width: 0">
                                        <div class="d-flex align-center ga-2">
                                            <span class="font-weight-medium text-truncate">
                                                {{ item._device?.asset_label || item._device?.slno || 'Tracker' }}
                                            </span>
                                            <v-chip size="x-small" variant="tonal" color="primary" label>
                                                <v-icon start icon="mdi-map-marker-path" size="12"/>Trip
                                            </v-chip>
                                        </div>
                                        <TripEventBadges :trip="item" class="mt-1"/>
                                        <div class="feed-time text-medium-emphasis">{{ formatTime(item.time) }}</div>
                                        <div class="text-caption text-medium-emphasis text-truncate">
                                            {{ item.from || '-' }} &rarr; {{ item.to || '-' }}
                                        </div>
                                    </div>
                                    <div class="text-right flex-shrink-0">
                                        <v-chip v-if="item.trip_score != null" size="x-small" variant="flat"
                                                :color="tripScoreColor(item.trip_score)" class="mb-1">
                                            {{ formatTripScore(item.trip_score) }}
                                        </v-chip>
                                        <v-chip v-else-if="insightsLockedOwner" size="x-small" variant="tonal"
                                                color="primary" class="mb-1" title="Unlock with Premium">
                                            <v-icon icon="mdi-lock" size="11"/>
                                        </v-chip>
                                        <div class="text-caption">
                                            {{ strOperations().unitifyDistance(item.distance, 'distance', 'km') }}
                                        </div>
                                        <div class="text-caption text-medium-emphasis">{{ formatDuration(item.duration) }}</div>
                                    </div>
                                </div>
                                <!-- Every trip shows its route map inline (lazy-loaded on scroll). -->
                                <div class="px-4 pb-3">
                                    <LazyTripMap :asset-id="item.asset_id" :trip-id="item.id" :height="190"/>
                                    <div class="d-flex align-center mt-2"
                                         :class="mileageEnabled ? 'justify-space-between' : 'justify-end'">
                                        <v-menu v-if="mileageEnabled">
                                            <template #activator="{ props }">
                                                <v-chip v-bind="props" size="small" label
                                                        variant="tonal"
                                                        :color="resolvedClass(item) === 0 ? 'secondary' : 'primary'"
                                                        :prepend-icon="resolvedClass(item) === 0 ? 'mdi-account-outline' : 'mdi-briefcase-outline'">
                                                    {{ classLabel(item) }}
                                                    <v-icon end icon="mdi-menu-down"/>
                                                </v-chip>
                                            </template>
                                            <v-list density="compact">
                                                <v-list-item title="Personal" prepend-icon="mdi-account-outline"
                                                             @click="classifyTrip(item, 0)"/>
                                                <v-list-item v-for="b in businessOptions" :key="b.slot"
                                                             :title="b.name" prepend-icon="mdi-briefcase-outline"
                                                             @click="classifyTrip(item, b.slot)"/>
                                            </v-list>
                                        </v-menu>
                                        <v-btn size="small" variant="tonal" prepend-icon="mdi-map-outline"
                                               @click.stop="openTrip(item)">Full map</v-btn>
                                    </div>
                                </div>
                            </v-card>

                            <!-- Stay card: dwell at a place between two trips -->
                            <v-card v-else-if="item._kind === 'stay'" variant="outlined" rounded="lg"
                                    class="feed-card stay-card mb-2">
                                <div class="d-flex align-center pa-3 ga-3">
                                    <v-avatar color="primary" variant="tonal" size="40" class="flex-shrink-0">
                                        <v-icon icon="mdi-parking" size="20"/>
                                    </v-avatar>
                                    <div class="flex-grow-1" style="min-width: 0">
                                        <div class="text-truncate">
                                            At <span class="font-weight-medium">{{ item.place || 'a place' }}</span>
                                        </div>
                                        <div class="feed-time text-medium-emphasis d-flex align-center ga-1">
                                            <v-icon icon="mdi-clock-outline" size="12"/>
                                            <span class="font-weight-medium">{{ formatDuration(item.seconds) }}</span>
                                            <span class="text-truncate">&middot; {{ formatTime(item.startTime) }} &ndash; {{ formatTime(item.endTime) }}</span>
                                        </div>
                                    </div>
                                </div>
                            </v-card>

                            <!-- Alert card -->
                            <v-card v-else variant="outlined" rounded="lg" class="feed-card alert-card mb-2"
                                    :class="{ 'alert-unread': !item.read }"
                                    :style="{ borderLeft: `4px solid ${alertColorForItem(item)}` }"
                                    @click="showNotificationDetails(item)">
                                <div class="d-flex align-center pa-3 ga-3">
                                    <v-avatar :color="alertColorForItem(item)" variant="tonal" size="40"
                                              class="flex-shrink-0">
                                        <v-icon :icon="alertIconForItem(item)" size="20"/>
                                    </v-avatar>
                                    <div class="flex-grow-1" style="min-width: 0">
                                        <div class="d-flex align-center ga-2">
                                            <span class="text-truncate" :class="{ 'font-weight-bold': !item.read }">
                                                {{ item.title || alertLabel(item) }}
                                            </span>
                                            <v-chip size="x-small" variant="tonal" label
                                                    :color="alertColorForItem(item)">
                                                {{ alertLabel(item) }}
                                            </v-chip>
                                            <!-- Red SOS only for a person's emergency (phone SOS or the
                                                 tracker's panic button). A tamper files as type 13 too
                                                 but is a "possible tampering" notice, not an SOS; its
                                                 label chip already says so. -->
                                            <v-chip v-if="isEmergencySos(item)"
                                                    size="x-small" color="error" variant="flat"
                                                    class="font-weight-black text-uppercase"
                                                    style="letter-spacing: 0.05em;">SOS</v-chip>
                                            <v-icon v-if="!item.read" icon="mdi-circle" size="8" color="error"/>
                                        </div>
                                        <div class="text-caption text-medium-emphasis text-truncate">
                                            <v-icon icon="mdi-map-marker-radius-outline" size="12"/>
                                            {{ item.asset || 'Tracker' }}
                                        </div>
                                        <div v-if="alertAddress(item)"
                                             class="text-caption text-medium-emphasis text-truncate"
                                             :title="alertAddress(item)">
                                            <v-icon icon="mdi-map-marker" size="12"/>
                                            {{ alertAddress(item) }}
                                        </div>
                                        <div class="feed-time text-medium-emphasis">{{ formatAgo(item.event_timestamp) }} · {{ formatTime(item.event_timestamp) }}</div>
                                    </div>
                                    <div class="d-flex align-center flex-shrink-0" @click.stop>
                                        <v-btn v-if="alertCoords(item) || item.asset_id" icon="mdi-map-marker"
                                               size="small" variant="text" color="primary"
                                               :title="alertCoords(item) ? 'Open location in Google Maps' : 'Show tracker on map'"
                                               @click="openAlertLocation(item)"/>
                                        <v-btn icon="mdi-information-outline" size="small" variant="text" color="primary"
                                               title="View details" @click="showNotificationDetails(item)"/>
                                        <v-btn :icon="item.read ? 'mdi-email-outline' : 'mdi-email-open'"
                                               :title="item.read ? 'Mark as unread' : 'Mark as read'"
                                               size="small" variant="text" @click="toggleRead(item)"/>
                                    </div>
                                </div>
                            </v-card>
                        </div>
                    </div>

                    <div v-if="hasMoreAlerts" class="text-center py-2">
                        <v-btn variant="text" size="small" :loading="loadingMore"
                               prepend-icon="mdi-refresh" @click="loadMoreAlerts">Load more alerts</v-btn>
                    </div>

                    <HistoryUnlockCard v-if="historyLocked" class="mt-2"
                                       :upgrade-days="historyUpgradeDays"
                                       :can-upgrade="canUpgradePlan"
                                       @upgrade="goToPlans"/>

                    <!-- Driver-insights paywall: owner-only upsell (members viewing
                         another circle just see the values hidden). -->
                    <DriverInsightsUnlockCard v-if="insightsLockedOwner" class="mt-2"
                                              @upgrade="goToPlans"/>
                </template>

                <div v-else class="text-center pa-10">
                    <v-icon size="48" color="grey-lighten-1" class="mb-3">mdi-timeline-text-outline</v-icon>
                    <p class="text-body-1 text-medium-emphasis mb-1">No activity yet</p>
                    <p class="text-body-2 text-medium-emphasis mb-0">Trips and alerts from your trackers will show up here</p>
                </div>
            </v-col>
        </v-row>

        <!-- Trip full-map dialog -->
        <v-dialog v-model="tripDialog" max-width="900" scrollable>
            <v-card rounded="lg">
                <v-card-title class="d-flex justify-space-between align-center">
                    <span class="text-h6 text-truncate">
                        {{ selectedDevice?.asset_label || selectedDevice?.slno || 'Trip' }}
                    </span>
                    <v-btn icon="mdi-close" size="small" variant="text" @click="tripDialog = false"/>
                </v-card-title>
                <v-divider/>
                <v-card-text style="height: 520px" class="pa-0">
                    <TripRouteMap v-if="selectedDevice && selectedTrip"
                                  :selected-device="selectedDevice" :selected-trip="selectedTrip"/>
                </v-card-text>
            </v-card>
        </v-dialog>

        <!-- Alert details dialog: parsed payload, not raw email HTML -->
        <v-dialog v-if="dialogSwitch && selectedNotification" v-model="dialogSwitch" max-width="700" scrollable>
            <v-card rounded="lg">
                <v-card-title class="d-flex justify-space-between align-center pa-4">
                    <span class="text-h6 text-truncate">{{ selectedNotification.title || 'Alert Details' }}</span>
                    <v-btn icon="mdi-close" variant="text" size="small" @click="dialogSwitch = false"/>
                </v-card-title>
                <v-divider/>
                <v-card-text class="pa-3">
                    <AlertPayloadDetails :notification="selectedNotification"/>
                </v-card-text>
            </v-card>
        </v-dialog>

        <!-- Clear-all confirm -->
        <v-dialog v-model="clearConfirmDialog" max-width="400">
            <v-card rounded="lg">
                <v-card-title class="text-h6 pa-4">Clear all alerts</v-card-title>
                <v-card-text class="pb-2">
                    Are you sure you want to clear all alerts? This cannot be undone. Trips are not affected.
                </v-card-text>
                <v-card-actions class="pa-4 pt-0">
                    <v-spacer/>
                    <v-btn variant="text" @click="clearConfirmDialog = false">Cancel</v-btn>
                    <v-btn color="error" variant="tonal" @click="clearAll">Clear alerts</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script setup>
import {computed, onBeforeUnmount, onMounted, provide, ref, watch} from "vue";
import {useRoute, useRouter} from "vue-router";
import {send, unreadNotificationsCount} from "@/composables/apiClient";
import {strOperations} from "@/composables/globals";
import {shouldAutoMarkRead, observe} from "@/composables/autoReadAlerts";
import {useSnackbarStore} from "@/stores/snackBarStore";
import {useAuthStore} from "@/stores/authStore";
import {formatDateTime} from "@/composables/useTimezone";
import {useTripScore} from "@/composables/useTripScore";
import {
    alertLabel, alertIcon, alertColor, isEmergencySos, payloadOf, coordsOf,
} from "@/composables/alertTypes";
import {geocodeLatLng} from "@/composables/useGeocode";
import SubjectAvatar from "@/components/SubjectAvatar.vue";
import TripRouteMap from "@/components/cards/TripRouteMap.vue";
import LazyTripMap from "@/components/cards/devices/LazyTripMap.vue";
import TripEventBadges from "@/components/cards/devices/TripEventBadges.vue";
import AlertPayloadDetails from "@/components/cards/AlertPayloadDetails.vue";
import HistoryUnlockCard from "@/components/cards/HistoryUnlockCard.vue";
import DriverInsightsUnlockCard from "@/components/cards/DriverInsightsUnlockCard.vue";

// TripRouteMap injects latestLocations for live trip updates; provide a stub
// since this page is not in the Live (MQTT) context.
provide('latestLocations', ref(new Map()));

const {showSnackbar} = useSnackbarStore();
const {tripScoreColor, formatTripScore} = useTripScore();
const router = useRouter();
const route = useRoute();
const authStore = useAuthStore();

const loading = ref(true);
const loadingMore = ref(false);
const trips = ref([]);
const alerts = ref([]);

// Server-sent history gate hint ({history_days, upgrade_days, history_locked}),
// attached to the notifications response. Drives the "Unlock by upgrade" card
// so go-web does not silently show a truncated feed once entitlement
// enforcement is on. All-open (history_locked false) until then, so the card
// stays hidden today.
const historyHint = ref(null);
const historyLocked = computed(() => historyHint.value?.history_locked === true);

// Driver-insights paywall (server-computed on the trips response). When locked,
// the server already nulls trip_score/analytics so values are hidden for everyone;
// only the ACTIVE circle's OWNER additionally sees a padlock + "Unlock" upsell
// (a member viewing someone else's circle just sees the values hidden - they
// can't buy a plan for that circle).
const insightsLocked = ref(false);
const insightsLockedOwner = computed(() => insightsLocked.value && authStore.isCircleOwner === true);
const historyUpgradeDays = computed(() => {
    const d = historyHint.value?.upgrade_days;
    return typeof d === "number" ? d : null;
});
const canUpgradePlan = computed(() => authStore.isCircleOwner === true);
const goToPlans = () => router.push({name: "plans"});
const devicesByAsset = ref({});

// Mileage-log classification overlay (parity with the app). Business names by
// slot (index 0 = Business 1..3 = 4). Unclassified trips default to the primary
// (lowest non-empty) slot; only shown when the user has enabled the mileage log.
const mileageEnabled = ref(false);
const businesses = ref([null, null, null, null]);
const businessOptions = computed(() =>
    businesses.value
        .map((name, i) => ({slot: i + 1, name: (name || "").trim()}))
        .filter((b) => b.name));
const primarySlot = computed(() => {
    const found = businesses.value.findIndex((b) => (b || "").trim());
    return found === -1 ? 1 : found + 1;
});
const resolvedClass = (trip) => {
    const raw = trip.trip_class;
    if (raw === null || raw === undefined) return primarySlot.value;
    const c = Number(raw);
    // Corrupt/out-of-range value falls back to the primary business.
    return (Number.isInteger(c) && c >= 0 && c <= 4) ? c : primarySlot.value;
};
const classLabel = (trip) => {
    const c = resolvedClass(trip);
    if (c === 0) return "Personal";
    const name = (businesses.value[c - 1] || "").trim();
    return name || `Business ${c}`;
};
const loadMileageSettings = async () => {
    try {
        const {data} = await send("get", "/mileage/settings");
        const d = data?.data;
        if (d) {
            mileageEnabled.value = d.enabled === true;
            businesses.value = [d.business_1 || null, d.business_2 || null,
                d.business_3 || null, d.business_4 || null];
        }
    } catch (e) {
        // Optional overlay - leave classification hidden on failure.
    }
};
const classifyTrip = async (trip, cls) => {
    try {
        await send("put", `/trips/${trip.id}/class`, {trip_class: cls});
        // The feed renders shallow copies (allItems), so mutate the SOURCE trip
        // in trips.value - mutating the copy would not update the chip until reload.
        const src = trips.value.find((t) => t.id === trip.id && t.asset_id === trip.asset_id);
        if (src) src.trip_class = cls;
        showSnackbar("Trip classified");
    } catch (e) {
        showSnackbar("Could not classify trip", "error");
    }
};

// Type + member filters. typeFilter: all | trip | alert.
const typeFilter = ref('all');
const selectedMemberKey = ref(null);

// Alert pagination (the feed loads recent items; older alerts come via Load more).
const alertsPage = ref(1);
const alertsLastPage = ref(1);
const hasMoreAlerts = computed(() =>
    typeFilter.value !== 'trip' && alertsPage.value < alertsLastPage.value);

const tripDialog = ref(false);
const selectedDevice = ref(null);
const selectedTrip = ref(null);

const dialogSwitch = ref(false);
const selectedNotification = ref(null);
const clearConfirmDialog = ref(false);

// --- Alert visual mapping ---
// Label, icon and colour come from the shared alertTypes helper (type refined by
// sub_type), the same one AlertPayloadDetails uses, so a tamper, a charger event
// or a panic press look identical in the feed and in the detail dialog.
const alertColorForItem = (item) => alertColor(item);
const alertIconForItem = (item) => alertIcon(item);

// --- Member chips (a person may own several trackers; key on the member,
// falling back to the tracker when a device has no linked member). Built from
// both trips and alerts so filtering covers the whole feed. ---
const memberKeyOf = (device, assetId) => device?.member_user_id != null
    ? `u${device.member_user_id}`
    : `a${device?.asset_id ?? assetId}`;
const memberName = (device) => (device?.member_name || '').trim()
    || device?.asset_label || device?.slno || 'Tracker';
const memberChips = computed(() => {
    const seen = new Map();
    const addDevice = (device, assetId, fallbackName) => {
        const key = memberKeyOf(device, assetId);
        if (!seen.has(key)) {
            seen.set(key, {
                key,
                name: device ? memberName(device) : (fallbackName || 'Tracker'),
                photo: device?.member_photo || device?.asset_photo || null,
            });
        }
    };
    for (const t of trips.value) addDevice(t._device, t.asset_id);
    for (const a of alerts.value) addDevice(a._device, a.asset_id, a.asset);
    // The feed keeps only the 60 newest trips, so a quiet member's items can be
    // pushed off the end by everyone else's. While their filter is applied, hold
    // their chip open as long as the tracker still exists - otherwise the watcher
    // below would drop the user back to Everyone mid-read, with no explanation.
    if (selectedMemberKey.value != null && !seen.has(selectedMemberKey.value)) {
        const device = Object.values(devicesByAsset.value)
            .find((d) => memberKeyOf(d, d.asset_id) === selectedMemberKey.value);
        if (device) addDevice(device, device.asset_id);
    }
    return [...seen.values()];
});

// If a reload drops the selected member, fall back to Everyone.
watch(memberChips, (chips) => {
    if (selectedMemberKey.value != null && !chips.some((c) => c.key === selectedMemberKey.value)) {
        selectedMemberKey.value = null;
    }
});

const matchesMember = (device, assetId) => selectedMemberKey.value == null
    || memberKeyOf(device, assetId) === selectedMemberKey.value;

const filteredTrips = computed(() => trips.value.filter((t) => matchesMember(t._device, t.asset_id)));
const filteredAlerts = computed(() => alerts.value.filter((a) => matchesMember(a._device, a.asset_id)));

const unreadCount = computed(() => alerts.value.filter((a) => !a.read).length);
const hasUnread = computed(() => unreadCount.value > 0);

// Server timestamps are UTC but may lack a suffix; force UTC parsing.
const parseTime = (t) => {
    if (!t) return 0;
    const s = typeof t === 'string' && !t.endsWith('Z') ? `${t}Z` : t;
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? 0 : d.getTime();
};

const formatDuration = (sec) => {
    const s = Number(sec) || 0;
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    return h ? `${h}h ${m}m` : `${m}m`;
};

// Drop unhelpful place strings ("unknown"/"null"/a bare "lat,lon") so the stay
// card falls back to "a place" instead of printing junk (mirrors the app's _str).
const sanitizePlace = (p) => {
    const s = String(p ?? '').trim();
    if (!s) return '';
    const low = s.toLowerCase();
    if (low === 'unknown' || low === 'null') return '';
    if (/^-?\d+\.\d+\s*,\s*-?\d+\.\d+/.test(s)) return '';
    return s;
};

// Time-only (the day is already shown as the group header), so cards stay light.
const formatTime = (ts) => formatDateTime(ts, {hour: 'numeric', minute: '2-digit', hour12: true, year: undefined, month: undefined, day: undefined, second: undefined});

// Reactive "now", nudged every minute (see onMounted) so the relative "5m ago"
// labels advance on their own while the screen sits open.
const nowTick = ref(Date.now());

// Relative age ("20h ago") paired with the clock time on notification cards,
// matching the app.
const formatAgo = (ts) => {
    const ms = parseTime(ts);
    if (!ms) return '';
    const m = Math.floor((nowTick.value - ms) / 60000);
    if (m < 1) return 'just now';
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
};

const keyOf = (trip) => `${trip.asset_id}-${trip.id}`;

// --- Unified, time-sorted feed of trips + alerts ---
const allItems = computed(() => {
    const items = [];
    if (typeFilter.value !== 'alert') {
        for (const t of filteredTrips.value) {
            items.push({...t, _kind: 'trip', _ts: parseTime(t.time), _key: `t-${keyOf(t)}`});
            // Life360-style "stay" card directly under the departing trip: how long
            // the subject sat at the start place since the previous trip ended.
            // Skip id-less trips so a synthetic stay can't collide on key or attach
            // to the wrong trip.
            const stay = t.id ? dwellMap.value[keyOf(t)] : null;
            if (stay) {
                const startMs = parseTime(t.time) - stay.seconds * 1000;
                // Same "UTC, no suffix" shape as the API times so formatTime treats
                // it identically to trip.time.
                const startTime = new Date(startMs).toISOString().slice(0, 19).replace('T', ' ');
                items.push({
                    _kind: 'stay',
                    // Same _ts as its trip: a stable sort keeps it directly below the
                    // trip it was pushed after, and it stays in the trip's day group
                    // even when the trip starts at local midnight.
                    _ts: parseTime(t.time),
                    _key: `s-${keyOf(t)}`,
                    seconds: stay.seconds,
                    place: sanitizePlace(stay.place),
                    startTime,
                    endTime: t.time,
                });
            }
        }
    }
    if (typeFilter.value !== 'trip') {
        for (const a of filteredAlerts.value) {
            items.push({...a, _kind: 'alert', _ts: parseTime(a.event_timestamp), _key: `a-${a.id}`});
        }
    }
    return items.sort((x, y) => y._ts - x._ts);
});

// formatDateTime merges opts over time-bearing defaults, so date/time-only
// callers must null out the parts they don't want (mirrors formatDate).
const DATE_ONLY = {year: 'numeric', month: 'numeric', day: 'numeric', hour: undefined, minute: undefined, second: undefined};
// Date-only key in the user's timezone for day grouping (no time, or every item
// would key to its own group and print a header before each card).
const dayKey = (ms) => formatDateTime(new Date(ms), DATE_ONLY);
const groupedItems = computed(() => {
    const now = new Date();
    const todayKey = dayKey(now.getTime());
    const yesterdayKey = dayKey(now.getTime() - 86400000);
    const groups = [];
    const indexByKey = {};
    for (const item of allItems.value) {
        const k = dayKey(item._ts);
        if (!(k in indexByKey)) {
            let label = k;
            if (k === todayKey) label = 'Today';
            else if (k === yesterdayKey) label = 'Yesterday';
            else label = formatDateTime(new Date(item._ts), {weekday: 'short', month: 'short', day: 'numeric', year: undefined, hour: undefined, minute: undefined, second: undefined});
            indexByKey[k] = groups.length;
            groups.push({key: k, label, items: []});
        }
        groups[indexByKey[k]].items.push(item);
    }
    return groups;
});

// --- Weekly summary: rolling 7-day windows (this week vs prior week) ---
const WEEK_MS = 7 * 86400000;
const aggregate = (arr) => {
    const scored = arr.filter((t) => t.trip_score != null);
    return {
        drives: arr.length,
        distance: arr.reduce((s, t) => s + (Number(t.distance) || 0), 0),
        topSpeed: arr.reduce((m, t) => Math.max(m, Number(t.max_speed) || 0), 0),
        avgScore: scored.length ? scored.reduce((s, t) => s + Number(t.trip_score), 0) / scored.length : null,
    };
};
const weeklyStats = computed(() => {
    const now = Date.now();
    const thisWeek = filteredTrips.value.filter((t) => parseTime(t.time) > now - WEEK_MS);
    const lastWeek = filteredTrips.value.filter((t) => {
        const ms = parseTime(t.time);
        return ms <= now - WEEK_MS && ms > now - 2 * WEEK_MS;
    });
    return {thisWeek: aggregate(thisWeek), lastWeek: aggregate(lastWeek)};
});
const trend = (cur, prev, goodWhenUp = null) => {
    if (cur == null || prev == null) return null;
    if (cur === prev) return {icon: 'mdi-minus', color: 'grey', title: 'No change vs last week'};
    const up = cur > prev;
    const color = goodWhenUp == null ? 'grey' : (up === goodWhenUp ? 'success' : 'error');
    return {icon: up ? 'mdi-arrow-up' : 'mdi-arrow-down', color, title: `${up ? 'Up' : 'Down'} vs last week`};
};
const weeklyTiles = computed(() => {
    const t = weeklyStats.value.thisWeek;
    const l = weeklyStats.value.lastWeek;
    const ops = strOperations();
    return [
        {label: 'Drives', value: String(t.drives), trend: trend(t.drives, l.drives)},
        {label: 'Distance', value: t.drives ? ops.unitifyDistance(t.distance, 'distance', 'km') : '--', trend: trend(t.distance, l.distance)},
        {label: 'Top speed', value: t.topSpeed ? ops.unitifyDistance(t.topSpeed, 'speed', 'miles') : '--', trend: trend(t.topSpeed, l.topSpeed, false)},
        {label: 'Avg score', value: t.avgScore != null ? formatTripScore(t.avgScore) : '--', trend: trend(t.avgScore, l.avgScore, true)},
    ];
});

// --- Dwell-before: how long the member sat at the start location since their
// previous trip ended - a "where they lingered" insight from trip gaps. ---
const dwellMap = computed(() => {
    const byDevice = {};
    for (const t of filteredTrips.value) {
        (byDevice[t.asset_id] ??= []).push(t);
    }
    const map = {};
    for (const list of Object.values(byDevice)) {
        const asc = [...list].sort((a, b) => parseTime(a.time) - parseTime(b.time));
        for (let i = 1; i < asc.length; i++) {
            const prevDur = Number(asc[i - 1].duration);
            if (!(prevDur > 0)) continue;
            const prevEnd = parseTime(asc[i - 1].time) + prevDur * 1000;
            const gap = parseTime(asc[i].time) - prevEnd;
            if (gap > 5 * 60000) {
                map[keyOf(asc[i])] = {seconds: Math.round(gap / 1000), place: asc[i].from};
            }
        }
    }
    return map;
});

const placeTotals = computed(() => {
    const totals = {};
    for (const {seconds, place} of Object.values(dwellMap.value)) {
        if (!place) continue;
        const full = String(place).trim();
        if (/^-?\d+\.\d+\s*,\s*-?\d+\.\d+/.test(full)) continue;
        totals[full] = (totals[full] || 0) + seconds;
    }
    return Object.entries(totals)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([place, seconds]) => ({place: place.split(',').slice(0, 2).join(',').trim().slice(0, 26), seconds}));
});

const openTrip = (trip) => {
    selectedDevice.value = trip._device;
    selectedTrip.value = trip;
    tripDialog.value = true;
};

// --- Alert actions ---
const showNotificationDetails = (notification) => {
    selectedNotification.value = notification;
    dialogSwitch.value = true;
};

// Hardware alerts carry latitude/longitude; a phone SOS only lat/lng. The
// payload may also arrive as a JSON string. coordsOf handles both.
const alertCoords = (item) => coordsOf(payloadOf(item));

// Reverse-geocoded street address per alert location, resolved in the BACKGROUND
// and shown in the card. useGeocode throttles Nominatim to <=1 req/sec and caches
// in localStorage, so a feed of alerts resolves progressively without spamming
// the endpoint. Keyed by rounded coords so alerts at the same spot share one
// lookup. Client-side only - we deliberately do NOT persist these back to our
// server (Nominatim's policy discourages bulk storage, and the localStorage cache
// already dedups across sessions).
const alertAddresses = ref({});
const addrKey = (lat, lng) => `${(+lat).toFixed(5)},${(+lng).toFixed(5)}`;
const alertAddress = (item) => {
    const c = alertCoords(item);
    return c ? (alertAddresses.value[addrKey(c.lat, c.lng)] || null) : null;
};
const resolveAlertAddresses = (list) => {
    for (const item of list) {
        const c = alertCoords(item);
        if (!c) continue;
        const k = addrKey(c.lat, c.lng);
        if (k in alertAddresses.value) continue; // already resolved or in-flight
        alertAddresses.value[k] = null; // reserve so we don't double-fetch
        geocodeLatLng({lat: c.lat, lon: c.lng}).then((addr) => {
            if (addr) alertAddresses.value = {...alertAddresses.value, [k]: addr};
        });
    }
};
watch(alerts, (list) => resolveAlertAddresses(list || []));

// The location icon opens Google Maps at the alert's actual coordinates when the
// payload carries them; otherwise it falls back to focusing the tracker on Live.
const openAlertLocation = (item) => {
    const coords = alertCoords(item);
    if (coords) {
        window.open(`https://www.google.com/maps?q=${coords.lat},${coords.lng}`, '_blank', 'noopener');
        return;
    }
    if (item.asset_id != null) {
        router.push({name: 'dashboard', query: {asset: String(item.asset_id)}});
    }
};

const toggleRead = (notification) => {
    send('put', `notifications/${notification.id}`)
        .then((response) => {
            if (response.data.success && response.data.data) {
                // The feed renders shallow copies (allItems), so mutate the source
                // alert in-place for the change to reflect + recompute the feed.
                const src = alerts.value.find((a) => a.id === notification.id);
                if (src) src.read = !src.read;
            }
        })
        .catch(() => showSnackbar(`"${notification.title}" could not be updated`, 'error'));
};

const readAll = () => {
    invalidateInFlight();
    send('post', `notifications/read`)
        .then((response) => {
            if (response.data.success) {
                alerts.value.forEach((a) => {
                    a.read = true;
                });
                showSnackbar(response.data.message);
            }
        })
        .catch(() => showSnackbar('Could not mark all read', 'error'));
};

// Opening the feed reads it. One POST per open, and again only when an alert
// the viewer has not seen in this open lands while the tab is visible; a poll
// tick that returns the same ids does nothing. The server marks ALL of the
// caller's rows (the same call as the button), so the local flip mirrors it.
// The 201 body carries unreadNotifications: 0, which the apiClient interceptor
// writes into the bell; the explicit zero covers the server's 30s count cache.
// Failure is silent: the button is still there, and the ids stay observed for
// this open, so the rows show unread until the next open (no retry storm).
const seenAlertIds = new Set();
let autoReadInFlight = false;
const autoMarkRead = () => {
    const visible = document.visibilityState === 'visible';
    const busy = autoReadInFlight || !!inFlight;
    const fire = shouldAutoMarkRead({alerts: alerts.value, seen: seenAlertIds, visible, inFlight: busy});
    // Observe only what a visible, settled tab has shown: a hidden tab has
    // shown nothing, and while a fetch or the POST is in flight the decision
    // is deferred to the load that lands next, which must still see these ids
    // as new or the open's mark is lost.
    if (visible && !busy) observe(alerts.value, seenAlertIds);
    if (!fire) return;
    autoReadInFlight = true;
    send('post', 'notifications/read')
        .then((response) => {
            if (!response.data.success) return;
            alerts.value.forEach((a) => {
                a.read = true;
            });
            unreadNotificationsCount.value = 0;
        })
        .catch(() => {})
        .finally(() => {
            autoReadInFlight = false;
        });
};
// The alerts ref is replaced (not mutated) by every list load, so this is one
// call per completed load; in-place read flips do not re-trigger it.
watch(alerts, autoMarkRead);

const clearAll = () => {
    clearConfirmDialog.value = false;
    invalidateInFlight();
    send('delete', `notifications/clear`)
        .then((response) => {
            if (response.data.success) {
                alerts.value = [];
                alertsPage.value = 1;
                alertsLastPage.value = 1;
                showSnackbar(response.data.message);
            }
        })
        .catch(() => showSnackbar('Could not clear alerts', 'error'));
};

// --- Data loading ---
const attachDevice = (notification) => ({...notification, _device: devicesByAsset.value[notification.asset_id] || null});

// The no-asset notifications endpoint returns a resource-collection paginator,
// so page info is nested under `meta` (not top-level). Fall back to top-level
// just in case the shape ever changes.
const pageInfo = (nData) => ({
    page: nData?.meta?.current_page ?? nData?.current_page ?? 1,
    lastPage: nData?.meta?.last_page ?? nData?.last_page ?? 1,
});

// Device metadata (asset_id, label, avatar) barely changes, and personal-trackers
// is unpaginated and the heaviest read in this portal, so it is loaded on mount
// and on a circle-data reload only - never on the periodic refresh tick.
// Tracked separately from the map being non-empty so a circle with no linked
// trackers doesn't re-request the list on every tick.
let trackersLoaded = false;
const loadTrackers = async (signal) => {
    const res = await send('get', 'personal-trackers', null, {signal});
    const map = {};
    (res.data?.data?.devices ?? [])
        .filter((d) => d.asset_id)
        .forEach((d) => {
            map[d.asset_id] = d;
        });
    devicesByAsset.value = map;
    trackersLoaded = true;
};

// A background refresh re-reads every alert page the user has open so "Load more"
// results survive, but a user who paged deep would otherwise turn one tick into
// many requests, so cap it. Pages past the cap are simply kept as-is (they are
// old alerts, they do not change).
const ALERT_REFRESH_PAGE_CAP = 5;

// One run at a time. Two overlapping runs both assign trips/alerts and the last
// to RESOLVE wins, not the last to start, so an older snapshot can overwrite a
// newer one. A user-initiated load supersedes (and aborts) a background one; a
// background tick simply steps aside.
let runSeq = 0;
let inFlight = null;
let lastFetchAt = 0;

// A mutation the server has just applied invalidates any refresh already in
// flight: that response was assembled before the change, so committing it would
// put the cleared/unread alerts straight back on screen.
const invalidateInFlight = () => {
    runSeq++;
    if (inFlight) {
        inFlight.abort();
        inFlight = null;
    }
};

// silent = background refresh: no spinner (the feed stays on screen and keeps its
// scroll offset), no pagination reset, no error snackbar.
const fetchActivity = async ({silent = false, refreshTrackers = !silent} = {}) => {
    if (silent && inFlight) return;
    if (inFlight) inFlight.abort();
    const controller = new AbortController();
    const signal = controller.signal;
    inFlight = controller;
    const mySeq = ++runSeq;
    const isCurrent = () => mySeq === runSeq;
    lastFetchAt = Date.now();

    if (!silent) {
        loading.value = true;
        // Reset before the fetch so a stale lock from a prior circle can't linger
        // if the new responses don't carry the insights sibling (defensive; circle
        // switches also fully reload the page).
        insightsLocked.value = false;
    }
    // Held locally and committed at the end so a background refresh never flickers
    // the paywall card open and shut mid-fetch.
    let nextInsightsLocked = false;
    let insightsSeen = false;
    try {
        if (refreshTrackers || !trackersLoaded) await loadTrackers(signal);
        const withAsset = Object.values(devicesByAsset.value);

        // Trips already on screen, per asset, so one asset's failure can fall back
        // to what we had instead of erasing that member (see the catch below).
        const prevTripsByAsset = {};
        for (const t of trips.value) {
            if (!prevTripsByAsset[t.asset_id]) prevTripsByAsset[t.asset_id] = [];
            prevTripsByAsset[t.asset_id].push(t);
        }

        const pagesToFetch = silent
            ? Math.min(Math.max(alertsPage.value, 1), ALERT_REFRESH_PAGE_CAP)
            : 1;

        const [tripResults, notifResults] = await Promise.all([
            Promise.all(withAsset.map((device) =>
                send('get', `assets/${device.asset_id}/trips`, {page: 1}, {signal})
                    .then((r) => {
                        // Driver-insights paywall flag (same for every asset of the
                        // active circle); the server has already withheld the values.
                        if (r.data?.data?.insights) {
                            insightsSeen = true;
                            nextInsightsLocked = r.data.data.insights.insights_locked === true;
                        }
                        return (r.data?.data?.data ?? []).map((trip) => ({...trip, _device: device}));
                    })
                    // A transient failure for ONE asset must not delete that member
                    // from the feed: losing their trips also loses their filter
                    // chip, and the memberChips watcher then silently resets the
                    // user's filter back to Everyone. Keep what we already had.
                    .catch(() => prevTripsByAsset[device.asset_id] || [])
            )),
            Promise.all(Array.from({length: pagesToFetch}, (unused, i) =>
                send('get', 'assets/notifications', {page: i + 1}, {signal}).catch(() => null))),
        ]);

        if (!isCurrent()) return;

        const allTrips = tripResults.flat();
        allTrips.sort((a, b) => parseTime(b.time) - parseTime(a.time));
        trips.value = allTrips.slice(0, 60);

        const nData = notifResults[0]?.data?.data;
        // A failed page 1 on a background tick must not blank the history hint.
        if (nData || !silent) historyHint.value = nData?.history ?? null;

        const fetchedIds = new Set();
        const fetched = [];
        for (const r of notifResults) {
            for (const n of (r?.data?.data?.data ?? [])) {
                // Pages shift as new alerts land between the requests.
                if (fetchedIds.has(n.id)) continue;
                fetchedIds.add(n.id);
                fetched.push(attachDevice(n));
            }
        }
        const info = pageInfo(nData);
        if (silent) {
            // Leave alertsPage alone: overwriting it would throw away every page
            // the user pulled in with "Load more". The refetched pages are a
            // contiguous newest-first window, so only alerts OLDER than that
            // window are carried over - anything inside it that the server no
            // longer returns has been cleared, and a plain union by id would
            // resurrect it on screen for the rest of the session.
            // parseTime yields 0 for a missing or unparseable stamp (alerts like
            // that are expected here - formatAgo guards for them), and a zero
            // boundary would discard every carried-over alert, so fall back to
            // keeping everything whenever the boundary can't be trusted.
            const stamps = fetched.map((a) => parseTime(a.event_timestamp));
            const oldest = stamps.length && stamps.every(Boolean) ? Math.min(...stamps) : 0;
            let retained;
            if (!notifResults.every(Boolean) || (fetched.length && !oldest)) {
                // A page failed, or the boundary is unusable: "missing" no longer
                // implies "deleted", so keep everything and let a later tick trim.
                retained = alerts.value.filter((a) => !fetchedIds.has(a.id));
            } else if (oldest) {
                // <= rather than <: one event can emit several alerts in the same
                // second (a geofence and a departure), so a tie group straddling
                // the page boundary must survive. Over-retaining a tie is undone
                // by a later tick; dropping a live alert would not come back.
                retained = alerts.value.filter((a) =>
                    !fetchedIds.has(a.id) && parseTime(a.event_timestamp) <= oldest);
            } else {
                // Every requested page answered and page 1 is empty: nothing left.
                retained = [];
            }
            // Re-attach devices to the carried-over alerts too, so a tracker
            // renamed since they loaded doesn't leave two labels (and two member
            // chips) for the same person in one list.
            alerts.value = [...fetched, ...retained.map(attachDevice)];
            if (nData) alertsLastPage.value = info.lastPage;
        } else {
            alerts.value = fetched;
            alertsPage.value = info.page;
            alertsLastPage.value = info.lastPage;
        }

        if (insightsSeen || !silent) insightsLocked.value = nextInsightsLocked;
    } catch (e) {
        // A superseded/aborted run and a background failure are both silent. The
        // clock was stamped at the start of the run, so a failure waits out a
        // full interval rather than retrying every tick.
        if (isCurrent() && !silent) showSnackbar('Activity could not be loaded', 'error');
    } finally {
        if (inFlight === controller) inFlight = null;
        // Only the current run owns the spinner; a superseded one leaving it up is
        // correct, because whoever superseded it is still loading. That does mean
        // invalidateInFlight() must never be called while a NON-silent run is up,
        // or the spinner would stick - both callers are behind :disabled="loading".
        if (isCurrent() && !silent) loading.value = false;
    }
};

const loadMoreAlerts = async () => {
    if (loadingMore.value || alertsPage.value >= alertsLastPage.value) return;
    loadingMore.value = true;
    try {
        const res = await send('get', 'assets/notifications', {page: alertsPage.value + 1});
        const nData = res?.data?.data;
        const more = (nData?.data ?? []).map(attachDevice);
        // Dedupe by id in case pages shift between requests.
        const seen = new Set(alerts.value.map((a) => a.id));
        alerts.value = [...alerts.value, ...more.filter((a) => !seen.has(a.id))];
        const info = pageInfo(nData);
        alertsPage.value = info.page;
        alertsLastPage.value = info.lastPage;
    } catch (e) {
        showSnackbar('Could not load more alerts', 'error');
    } finally {
        loadingMore.value = false;
    }
};

// ===== Background refresh =====
// The feed is assembled client-side from several endpoints and there is no MQTT
// signal for a new trip or alert, so freshness comes from polling. Three
// cadences, all driven off lastFetchAt by ONE tick that decides whether a fetch
// is due - swapping between a 2-minute and a 5-minute timer would need more state
// and would still be wrong across a visibility change. The tick is deliberately
// well above the browser's background-throttle floor.
const ACTIVE_REFRESH_MS = 2 * 60 * 1000;
const IDLE_REFRESH_MS = 5 * 60 * 1000;
// No mouse/touch/key for this long means the person is reading, or gone.
const IDLE_AFTER_MS = 2 * 60 * 1000;
const SCHEDULER_TICK_MS = 30 * 1000;
// Same list as Live.vue's watch heartbeat, for the same reason: mousemove and
// wheel never fire on finger input, so without the touch events a tablet user
// would always read as idle.
const ACTIVITY_EVENTS = ['mousemove', 'pointerdown', 'wheel', 'touchstart', 'touchmove', 'keydown'];

let ageInterval = null;
let schedulerTimer = null;
let lastActivityAt = Date.now();

const onUserActivity = () => {
    lastActivityAt = Date.now();
};

const refreshInterval = () => (Date.now() - lastActivityAt > IDLE_AFTER_MS
    ? IDLE_REFRESH_MS
    : ACTIVE_REFRESH_MS);

const maybeRefresh = () => {
    if (document.visibilityState !== 'visible') return;
    if (Date.now() - lastFetchAt < refreshInterval()) return;
    fetchActivity({silent: true});
};

const startScheduler = () => {
    if (schedulerTimer) return;
    schedulerTimer = setInterval(maybeRefresh, SCHEDULER_TICK_MS);
};

const stopScheduler = () => {
    if (schedulerTimer) {
        clearInterval(schedulerTimer);
        schedulerTimer = null;
    }
};

const onVisibilityChange = () => {
    if (document.visibilityState !== 'visible') {
        // A hidden tab does nothing at all: browsers clamp background timers to
        // once a minute and freeze them outright after a few minutes, so a timer
        // here would be unreliable as well as wasteful.
        stopScheduler();
        return;
    }
    // Coming back to the tab is itself a sign of use, and the catch-up below is
    // what actually makes the idle cadence work - the elapsed hidden time is
    // measured on return rather than counted by a timer that was never running.
    lastActivityAt = Date.now();
    // A load that finished while hidden was never shown; now it is.
    autoMarkRead();
    maybeRefresh();
    startScheduler();
};

// The reload socket (useCompanyReload, owned by the Auth layout) publishes
// c/{companyId}/reload on device/asset/zone/config saves. It does NOT carry new
// trips or alerts - those only arrive on the poll above - but it is already
// connected on this route, so honouring it costs nothing and picks up a renamed
// tracker or a new member straight away. Debounced so a burst of saves is one
// fetch, and it re-reads personal-trackers because device metadata is exactly
// what changed.
let reloadTimer = null;
const onCompanyDataReload = () => {
    clearTimeout(reloadTimer);
    reloadTimer = setTimeout(() => {
        // Mark the metadata stale as well as asking for it: if a scheduled tick
        // is already running, the call below steps aside, and without this the
        // request to re-read trackers would be dropped and never retried.
        trackersLoaded = false;
        fetchActivity({silent: true, refreshTrackers: true});
    }, 800);
};

onMounted(() => {
    // Deep-link from the live-map popup's "Activity" button: focus one member/
    // tracker. The chip only exists once its trips/alerts load, so setting the key
    // now is fine - the filter applies when the data arrives, and the memberChips
    // watcher clears it back to Everyone if that tracker never appears.
    if (route.query.focus) selectedMemberKey.value = String(route.query.focus);
    fetchActivity();
    loadMileageSettings();
    // Advance the relative "5m ago" labels once a minute while the screen is open.
    ageInterval = setInterval(() => { nowTick.value = Date.now(); }, 60000);
    document.addEventListener('visibilitychange', onVisibilityChange);
    ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, onUserActivity, {passive: true}));
    window.addEventListener('company-data-reload', onCompanyDataReload);
    // A tab opened in the background starts its scheduler on the first
    // visibilitychange instead, so we don't leave a no-op timer running.
    if (document.visibilityState === 'visible') startScheduler();
});
onBeforeUnmount(() => {
    if (ageInterval) clearInterval(ageInterval);
    stopScheduler();
    clearTimeout(reloadTimer);
    document.removeEventListener('visibilitychange', onVisibilityChange);
    ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, onUserActivity));
    window.removeEventListener('company-data-reload', onCompanyDataReload);
    // Retire the run first so the abort below lands as "superseded" and can't
    // snackbar an error at whatever page the user navigated to.
    runSeq++;
    if (inFlight) inFlight.abort();
});
</script>

<style scoped>
.feed-card {
    transition: box-shadow 0.15s ease, border-color 0.15s ease;
}

/* Only the alert card is clickable (opens its detail); the trip card is not -
   its map is inline - so the pointer affordance is scoped to the alert card. */
.alert-card {
    cursor: pointer;
}

.alert-card:hover {
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
}

.alert-unread {
    background: rgba(230, 3, 140, 0.04);
}

.dwell-line {
    color: #0891b2;
}

.feed-time {
    font-size: 11px;
    line-height: 1.3;
    opacity: 0.75;
}

/* The .max-height container is a fixed-height region with no scroll of its own,
   so a long feed would spill out and clip the last card. Scroll it internally. */
.activity-scroll {
    overflow-y: auto;
}

.type-toggle {
    height: 34px;
}
</style>
